import BlackShoppingCart from "@dana/Icons/BlackShoppingCart";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import ProductAddToCartButton from "@dana/Overrides/Components/ProductAddToCartButton";
import { getProductRealTimePrice } from "@dana/Overrides/Services/ProductServiceV2";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import { EZIDAttributeFilter } from "@dana/Types/EZIDAttributeFilter";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { get, post } from "@insite/client-framework/Services/ApiService";
import { getProductById } from "@insite/client-framework/Services/ProductServiceV2";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import { DataTableBody, DataTableCell, DataTableRow, Link } from "@insite/mobius/index";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface ResultScreenProps {
    backLabel?: string;
    readyshaftCategoryId?: string;
    spicerReadyShaftBrandId?: string;
    currentAttributeFilters?: EZIDAttributeFilter[];
    productDefaultImage?: string;
    productDefaultImageAltText?: string;
    promotionalImage?: string;
    promotionalImageAltText?: string;
    promotionalText?: string;
    promotionalURL?: LinkFieldValue;
    viewAdditionalDetailLabel?: string;
    onProceedToPreviousScreen: (newAttributeFilters?: EZIDAttributeFilter[]) => void;
}

const mapStateToProps = (state: ApplicationStateDana) => {
    const session = getSession(state);

    return {
        isAuthenticated: session?.isAuthenticated && !session?.isGuest,
        currencySymbol: state.context.session.currency?.currencySymbol ?? "",
    };
};

type Props = ResultScreenProps & ReturnType<typeof mapStateToProps>;

export interface ResultScreenStyles {
    columnWrapper?: InjectableCss;
    rowWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    addToCartButtonWrapper?: InjectableCss;
    backButton?: ButtonPresentationProps;
    resultProductImage?: ImgProps;
    promoImage?: ImgProps;
    brandLogoImage?: ImgProps;
    partNumberText?: TypographyPresentationProps;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    attributeTable?: DataTablePresentationProps;
    attributeTableCell?: DataTableCellProps;
    uom?: TypographyPresentationProps;
    orderType?: TypographyPresentationProps;
    cartIcon?: IconPresentationProps;
}

export const resultScreenStyles: ResultScreenStyles = {
    columnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: 24px;
            margin-top: 18px;
            margin-bottom: 0;
            margin-left: 0;
            margin-right: 0;
        `,
    },
    rowWrapper: {
        css: css`
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    width: 100%;
                    gap: 16px;
                    margin-top: 24px;
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                                                flex-direction: column;
                                                align-items: stretch;
                                                gap: 12px;
                                            `,
                        css`
                                                flex-direction: column;
                                                align-items: stretch;
                                                gap: 14px;
                                            `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
                `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
            border: 1px solid ${themeBackgrounds.lightGray};
            padding: 16px;
            gap: 16px;
            box-sizing: border-box;            
        `,
    },
    addToCartButtonWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    backButton: {
        variant: "tertiary",
        css: css`
                align-self: flex-start;
                text-align: left;
            `,
        icon: {
            src: PrevArrowIcon,
            position: "left",
        },
    },

    resultProductImage: {
        css: css`
        width: auto;
        max-width: 100%;
        height: auto;
        max-height: 320px;
        object-fit: contain;
        display: block;
        margin-left: 0;
        align-self: flex-start;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-height: 180px;
                        `,
                        css`
                            max-height: 220px;
                        `,
                        css`
                            max-height: 260px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
    `,
    },
    partNumberText: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTable: {
        css: css`
            width: 100%;
            border-collapse: collapse;
            border: 1px solid ${themeBackgrounds.lightGray};
        `,
    },
    promoImage: {
        css: css`
            width: auto;
            max-height: 300px;
        `,
    },
    brandLogoImage: {
        css: css`
            width: auto;
            object-fit: contain;
            max-height: 40px;
            margin-left: 0;
            align-self: flex-start;
        `,
    },
    uom: {
        size: fontSizes.small,
        lineHeight: lineHeights.defaultLarge,
        weight: fontWeights.medium,
    },
    orderType: {
        size: fontSizes.medium,
        lineHeight: lineHeights.defaultLarge,
        weight: fontWeights.bold,
    },
    attributeTableCell: {
        css: css`
            word-break: break-word;
            white-space: normal;
        `,
    },
    cartIcon: {
        src: BlackShoppingCart,
        size: 45,
    },
};

const ResultScreen = ({
    backLabel = "Back",
    productDefaultImage = "",
    productDefaultImageAltText = "Product Image",
    promotionalImage = "",
    promotionalImageAltText = "Promotional Image",
    promotionalText = "",
    promotionalURL,
    readyshaftCategoryId,
    spicerReadyShaftBrandId,
    currentAttributeFilters,
    viewAdditionalDetailLabel = "View Additional Detail",
    isAuthenticated,
    currencySymbol,
    onProceedToPreviousScreen,
}: Props) => {
    const styles = resultScreenStyles;
    const [showLoadingOverlay, setShowLoadingOverlay] = React.useState(false);
    const promotionalLink = promotionalURL ? useGetLink(promotionalURL) : undefined;
    const history = useHistory();
    const { isInShell } = useShellContext();

    const [prodId, setProductId] = useState("");
    const [selectedProductModel, setSelectedProduct] = useState<ProductModel>();
    const [productInfoModel, setProductInfo] = useState<ProductInfo>();
    const [realTimePrice, setRealTimePrice] = useState("0.00");

    const productImage = selectedProductModel?.largeImagePath
        || selectedProductModel?.mediumImagePath || selectedProductModel?.smallImagePath || productDefaultImage;
    const productImageAltText = selectedProductModel?.imageAltText || productDefaultImageAltText;
    const spicerLogoImageUrl = selectedProductModel?.brand?.logoSmallImagePath || selectedProductModel?.brand?.logoLargeImagePath;

    const onBackButtonClick = () => {
        onProceedToPreviousScreen(undefined);
    }

    const selectProduct = async (productId: string) => {
        setShowLoadingOverlay(true);
        try {
            const productResult = await getProductById({ id: productId, expand: ["properties"] });
           
            setSelectedProduct(productResult);
        } catch (error) {
            console.error("Error fetching product by ID:", error);
        } finally {
            setShowLoadingOverlay(false);
        }
    };

    useEffect(() => {
        const getProductIdAtFinalStep = async () => {
            setShowLoadingOverlay(true);
                post("/api/v1/ezid/getFinalProduct", {
                        categoryId: readyshaftCategoryId,
                        attributeValueIds: currentAttributeFilters?.map(filter => filter.attributeValueId) || [],
                    }).then((response:any) => {
                       setProductId(response?.productId);
                }).catch((error) => {
                    console.error("Error fetching final product ID:", error);
                }).finally(()=>{
                    setShowLoadingOverlay(false);
                });
          
        };
        getProductIdAtFinalStep();
    }, []);

    useEffect(() => {
        if (prodId) {
            selectProduct(prodId);
        }
    }, [prodId]);

    useEffect(() => {
        const fetchPriceAndInfo = async () => {
            if (selectedProductModel) {
                setShowLoadingOverlay(true);
                let prodInfo = createFromProduct(selectedProductModel);
                prodInfo = {
                    ...prodInfo,
                    qtyOrdered: 1,
                };
                setProductInfo(prodInfo);

                // get realtime pricing
                try {
                    const realTimePricingResult = await getProductRealTimePrice({
                        productId: prodId,
                        qtyOrdered: 1,
                        unitOfMeasure: prodInfo.unitOfMeasure,
                    });
                    if (realTimePricingResult) {
                        setRealTimePrice(realTimePricingResult.extendedUnitListPrice?.toString());
                    }
                    setShowLoadingOverlay(false);
                } catch (e) {
                    setShowLoadingOverlay(false);
                    console.error("Error fetching real time price:", e);
                }
            }
        };
        fetchPriceAndInfo();
    }, [selectedProductModel]);

    const productContext: ProductContextModel = {
        product: selectedProductModel || {} as any,
        productInfo: productInfoModel || {} as any,
    };

    const onRedirectButtonClick = (url: string) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.open(url, "_blank", "noopener,noreferrer");
        } else {
            history.push(url);
        }
    };

    // Group filters by attributeTypeName
    const groupedFilters = React.useMemo(() => {
        if (!currentAttributeFilters) return [];
        const map = new Map<string, string[]>();
        currentAttributeFilters.forEach(filter => {
            if (!filter.attributeTypeName || !filter.attributeValueName) { return; }
            if (!map.has(filter.attributeTypeName)) {
                map.set(filter.attributeTypeName, []);
            }
            map.get(filter.attributeTypeName)!.push(filter.attributeValueName);
        });
        return Array.from(map.entries()).map(([attributeTypeName, valueNames]) => ({
            attributeTypeName,
            attributeValueNames: valueNames.join(", "),
        }));
    }, [currentAttributeFilters]);

    const danaStatusCode = productContext?.product?.properties?.danaStatusCode;
    const showAddToCart = productContext?.product?.properties?.showRtbCodeAddToCart;
    
    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                width: 100%;
            `}
        >
            <StyledWrapper {...styles.columnWrapper} className="wrapper">
                <Button
                    {...styles.backButton}
                    onClick={onBackButtonClick}>
                    {backLabel}
                </Button>
                {selectedProductModel?.productNumber && (
                    <Typography {...styles.partNumberText} as="h3">
                        Part# {selectedProductModel.productNumber}
                    </Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.rowWrapper} className="wrapper">
                <StyledWrapper {...styles.contentWrapper} className="content-wrapper">
                    <StyledWrapper {...styles.columnWrapper}>
                        {productImage && (
                            <Img src={productImage} alt={productImageAltText} {...styles.resultProductImage} />
                        )}
                        {spicerLogoImageUrl ? (
                            <Img src={spicerLogoImageUrl} alt="ReadyShaft" {...styles.brandLogoImage} />
                        ) : (
                            <i>ReadyShaft <sup>TM</sup></i>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.contentWrapper}>

                    <StyledWrapper>
                        <Typography>{selectedProductModel?.detail?.modelNumber}</Typography> -  
                        <Typography>{selectedProductModel?.productTitle}</Typography>
                    </StyledWrapper>
                    <Icon {...styles.cartIcon} />
                    {danaStatusCode === '2' && productContext?.productInfo?.qtyOrdered && isAuthenticated && showAddToCart && (
                        <ProductContext.Provider value={productContext}>
                            <Typography {...styles.orderType}>
                                {translate("Stock Order")}: {currencySymbol}
                                {realTimePrice}
                                <Typography {...styles.uom}> / {productContext?.productInfo?.unitOfMeasure}</Typography>
                            </Typography>
                            <ProductAddToCartButton extendedStyles={{ messageStylesWrapper: styles.addToCartButtonWrapper }} />
                        </ProductContext.Provider>
                    )}
                    <Button
                        variant="primary"
                        onClick={() => { onRedirectButtonClick(selectedProductModel?.canonicalUrl || "#"); }}>
                        {viewAdditionalDetailLabel}
                    </Button>
                </StyledWrapper>
                <DataTable {...styles.attributeTable}>
                    <DataTableBody>
                        {groupedFilters.map((group, index) => (
                            <DataTableRow key={index}>
                                <DataTableCell {...styles.attributeTableCell}>
                                    <Typography {...styles.attributeTypeLabelText}>
                                        {group.attributeTypeName}
                                    </Typography>
                                </DataTableCell>
                                <DataTableCell {...styles.attributeTableCell}>
                                    {group.attributeValueNames}
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            {promotionalLink && (
                <StyledWrapper {...styles.rowWrapper} className="wrapper">
                    <StyledWrapper {...styles.contentWrapper} className="content-wrapper">
                        {promotionalImage && (
                            <Link href={promotionalLink.url} target="_blank">
                                <Img
                                    src={promotionalImage}
                                    alt={promotionalImageAltText}
                                    {...styles.promoImage}
                                />
                            </Link>
                        )}
                        {promotionalText && (
                            <Typography {...styles.partNumberText} as="h3">
                                {promotionalText}
                            </Typography>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </LoadingOverlay>
    )
};

export default connect(mapStateToProps)(ResultScreen);